import {
  Add as AddIcon,
  Category as CategoryIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Store as StoreIcon,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Switch,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import {
  AdminDialog,
  AdminEmptyState,
  AdminFilterSummary,
  PageShell,
  AdminTableActions,
  AdminTableBodyState,
  AdminTablePanel,
  ErrorState,
} from "@/components/common";
import MuiAutocompleteMultiSelect from "@/components/common/MuiAutocompleteMultiSelect";
import { SortableTableCell } from "@/components/common/SortableTableCell";
import { getApiConfig } from "@/config/api.config";
import { useLocalizedTablePagination } from "@/hooks/useLocalizedTablePagination";
import { useTableSorting } from "@/hooks/useTableSorting";
import { CraveCategory, CraveCategoryService } from "@/services/features/CraveCategoryService";
import { Customer, CustomerService } from "@/services/features/CustomerService";
import { Store, StoreService } from "@/services/features/StoreService";
import { matchesCategorySearch } from "@/utils/categorySearch";
import {
  isCustomerCategoryRowsPerPage,
  readCustomerCategoryTableSelection,
  writeCustomerCategoryTableSelection,
} from "@/utils/customerCategoryTableSelectionSession";
import { formatInteger } from "@/utils/number";
import { formatStoreSelectorLabel } from "@/utils/store";
import { formatStoreDate, getBrowserTimezone } from "@/utils/storeTime";

import { useCategoryViewFilters } from "./CategoryViewFiltersContext";

interface CustomerOption {
  id: number;
  label: string;
}

type StoreLinkOption = Store | { id: null; label: string };

type SortField = "id" | "name" | "createdAt";

const craveCategoryService = CraveCategoryService.getInstance();
const inventoryApiUrl = getApiConfig().inventory;

interface CraveCategoriesProps {
  embedded?: boolean;
}

const CraveCategories: React.FC<CraveCategoriesProps> = ({ embedded = false }) => {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const locale = i18n.resolvedLanguage || i18n.language;
  const browserTimezone = getBrowserTimezone();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const localizedTablePagination = useLocalizedTablePagination();
  const customerService = CustomerService.getInstance();
  const storeService = useMemo(() => StoreService.getInstance(), []);

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerOption | null>(null);
  const [categories, setCategories] = useState<CraveCategory[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isStoresLoading, setIsStoresLoading] = useState(false);
  const [defaultStoreId, setDefaultStoreId] = useState<number | null>(null);
  const [storePickerCategoryName, setStorePickerCategoryName] = useState<string | null>(null);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);
  const sharedFilters = useCategoryViewFilters();
  const [localSearchQuery, setLocalSearchQuery] = useState("");
  const [localMatchWholeWords, setLocalMatchWholeWords] = useState(false);
  const searchQuery = sharedFilters?.searchQuery ?? localSearchQuery;
  const setSearchQuery = sharedFilters?.setSearchQuery ?? setLocalSearchQuery;
  const matchWholeWords = sharedFilters?.matchWholeWords ?? localMatchWholeWords;
  const setMatchWholeWords = sharedFilters?.setMatchWholeWords ?? setLocalMatchWholeWords;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [categoryToDelete, setCategoryToDelete] = useState<CraveCategory | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);
  const [isCategoryNameInvalid, setIsCategoryNameInvalid] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const categoryRequestIdRef = useRef(0);

  useEffect(() => {
    document.title = t("management.category.craveCategories.documentTitle");
  }, [t]);

  useEffect(() => {
    const fetchCustomers = async () => {
      setIsLoadingCustomers(true);
      try {
        const response = await customerService.getAllCustomers();
        setCustomers(response.success && response.data ? response.data : []);
      } catch {
        setCustomers([]);
      } finally {
        setIsLoadingCustomers(false);
      }
    };

    void fetchCustomers();
  }, [customerService]);

  useEffect(() => {
    let isCurrent = true;

    const fetchStores = async () => {
      setIsStoresLoading(true);
      try {
        const response = await storeService.getStores();
        if (isCurrent) {
          setStores(response.data ?? []);
        }
      } catch (error) {
        console.error("Error fetching stores:", error);
        if (isCurrent) {
          setStores([]);
        }
      } finally {
        if (isCurrent) {
          setIsStoresLoading(false);
        }
      }
    };

    void fetchStores();

    return () => {
      isCurrent = false;
    };
  }, [storeService]);

  useEffect(() => {
    const sharedSelection = readCustomerCategoryTableSelection();
    const customerIdParam = searchParams.get("customerId");
    const customerId =
      customerIdParam === null ? sharedSelection?.customerId : Number(customerIdParam);
    const customer = customers.find((item) => item.id === customerId);
    const pageParam = Number(searchParams.get("page"));
    const rowsPerPageParam = Number(searchParams.get("rowsPerPage"));
    const nextPage = Number.isInteger(pageParam) && pageParam > 0 ? pageParam - 1 : 0;
    const nextRowsPerPage = isCustomerCategoryRowsPerPage(rowsPerPageParam)
      ? rowsPerPageParam
      : (sharedSelection?.rowsPerPage ?? 50);

    setSelectedCustomer(customer ? { id: customer.id, label: customer.name } : null);
    setPage(nextPage);
    setRowsPerPage(nextRowsPerPage);

    const nextSearchParams = new URLSearchParams(searchParams);
    if (customers.length > 0) {
      if (customer) nextSearchParams.set("customerId", String(customer.id));
      else nextSearchParams.delete("customerId");
    }
    nextSearchParams.set("page", String(nextPage + 1));
    nextSearchParams.set("rowsPerPage", String(nextRowsPerPage));
    if (nextSearchParams.toString() !== searchParams.toString()) {
      setSearchParams(nextSearchParams, { replace: true });
    }
    if (customers.length > 0) {
      writeCustomerCategoryTableSelection({
        customerId: customer?.id ?? null,
        rowsPerPage: nextRowsPerPage,
      });
    }
  }, [customers, searchParams, setSearchParams]);

  const fetchCategories = useCallback(async (customerId: number) => {
    const requestId = ++categoryRequestIdRef.current;
    setIsLoadingCategories(true);
    setCategoriesError(null);
    try {
      const response = await craveCategoryService.getByCustomerId(customerId);
      if (requestId !== categoryRequestIdRef.current) return;

      if (response.success && response.data) {
        setCategories(response.data);
      } else {
        setCategories([]);
        setCategoriesError(response.error ?? "");
      }
    } catch {
      if (requestId !== categoryRequestIdRef.current) return;

      setCategories([]);
      setCategoriesError("");
    } finally {
      if (requestId === categoryRequestIdRef.current) {
        setIsLoadingCategories(false);
      }
    }
  }, []);

  const selectedCustomerId = selectedCustomer?.id;
  const customerStores = useMemo(
    () => stores.filter((store) => store.customerId === selectedCustomerId),
    [selectedCustomerId, stores],
  );

  useEffect(() => {
    setDefaultStoreId(null);
  }, [selectedCustomerId]);

  useEffect(() => {
    if (selectedCustomerId) {
      void fetchCategories(selectedCustomerId);
    } else {
      categoryRequestIdRef.current += 1;
      setCategories([]);
      setCategoriesError(null);
      setIsLoadingCategories(false);
    }

    return () => {
      categoryRequestIdRef.current += 1;
    };
  }, [fetchCategories, selectedCustomerId]);

  const openCategoryInStore = (storeId: number, categoryName: string) => {
    window.open(
      `${inventoryApiUrl}/sample/${storeId}/crave-category?craveCategory=${encodeURIComponent(categoryName)}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const handleCategoryNavigation = (categoryName: string) => {
    const defaultStore = customerStores.find((store) => store.id === defaultStoreId);
    if (defaultStore) {
      openCategoryInStore(defaultStore.id, categoryName);
      return;
    }

    setStorePickerCategoryName(categoryName);
  };

  const noDefaultStoreOption = useMemo<StoreLinkOption>(
    () => ({ id: null, label: t("management.category.craveCategories.options.noDefaultStore") }),
    [t],
  );
  const storeLinkOptions = useMemo<StoreLinkOption[]>(
    () => [noDefaultStoreOption, ...customerStores],
    [customerStores, noDefaultStoreOption],
  );
  const selectedDefaultStore = useMemo<StoreLinkOption>(
    () => customerStores.find((store) => store.id === defaultStoreId) ?? noDefaultStoreOption,
    [customerStores, defaultStoreId, noDefaultStoreOption],
  );
  const getStoreLinkOptionLabel = (option: StoreLinkOption) =>
    "label" in option ? option.label : formatStoreSelectorLabel(option, t, "CRAVE_ADMIN");

  const customerOptions = useMemo<CustomerOption[]>(
    () => customers.map((customer) => ({ id: customer.id, label: customer.name })),
    [customers],
  );
  const filteredCategories = useMemo(
    () =>
      categories.filter((category) =>
        matchesCategorySearch(category.name, searchQuery, matchWholeWords),
      ),
    [categories, matchWholeWords, searchQuery],
  );
  const { sortColumn, sortDirection, handleSort, sortedItems } = useTableSorting<
    CraveCategory,
    SortField
  >(filteredCategories, (category, column) => category[column], "name", locale);
  const paginatedItems = sortedItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const updatePagination = (nextPage: number, nextRowsPerPage = rowsPerPage) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("page", String(nextPage + 1));
    nextSearchParams.set("rowsPerPage", String(nextRowsPerPage));
    setSearchParams(nextSearchParams);
    setPage(nextPage);
  };

  const handleCustomerChange = (value: CustomerOption | null) => {
    categoryRequestIdRef.current += 1;
    const nextSearchParams = new URLSearchParams(searchParams);
    if (value) nextSearchParams.set("customerId", String(value.id));
    else nextSearchParams.delete("customerId");
    nextSearchParams.set("page", "1");
    nextSearchParams.set("rowsPerPage", String(rowsPerPage));
    setSearchParams(nextSearchParams);
    setSelectedCustomer(value);
    setDefaultStoreId(null);
    setCategories([]);
    setCategoriesError(null);
    setIsLoadingCategories(false);
    setPage(0);
    writeCustomerCategoryTableSelection({ customerId: value?.id ?? null, rowsPerPage });
  };

  const handleDelete = async () => {
    if (!categoryToDelete) return;
    setIsDeleting(true);
    setDeleteError(null);
    const response = await craveCategoryService.deleteCraveCategory(categoryToDelete.id);
    if (!response.success) {
      setDeleteError(response.error ?? t("management.category.craveCategories.errors.delete"));
      setIsDeleting(false);
      return;
    }

    const remainingCategories = categories.filter(
      (category) => category.id !== categoryToDelete.id,
    );
    setCategories(remainingCategories);
    const remainingFilteredCategories = remainingCategories.filter((category) =>
      matchesCategorySearch(category.name, searchQuery, matchWholeWords),
    );
    const maximumPage = Math.max(
      0,
      Math.ceil(remainingFilteredCategories.length / rowsPerPage) - 1,
    );
    if (page > maximumPage) updatePagination(maximumPage);
    setCategoryToDelete(null);
    setIsDeleting(false);
    enqueueSnackbar(t("management.category.craveCategories.notifications.deleted"), {
      variant: "success",
    });
  };

  const closeCreateDialog = () => {
    setIsCreateDialogOpen(false);
    setNewCategoryName("");
    setCreateError(null);
    setIsCategoryNameInvalid(false);
  };

  const handleCreate = async () => {
    const name = newCategoryName.trim();
    if (!name) {
      setIsCategoryNameInvalid(true);
      return;
    }
    if (!selectedCustomer) return;

    setIsCreating(true);
    setCreateError(null);
    const response = await craveCategoryService.create({ name, customerId: selectedCustomer.id });
    if (!response.success || !response.data) {
      setCreateError(response.error ?? t("management.category.craveCategories.errors.create"));
      setIsCreating(false);
      return;
    }

    const createdCategory = response.data;
    setCategories((current) => [...current, createdCategory]);
    setIsCreating(false);
    closeCreateDialog();
    enqueueSnackbar(t("management.category.craveCategories.notifications.created"), {
      variant: "success",
    });
  };

  const renderPagination = () => (
    <TablePagination
      component="div"
      count={filteredCategories.length}
      page={page}
      onPageChange={(_event, nextPage) => updatePagination(nextPage)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(event) => {
        const nextRowsPerPage = Number(event.target.value);
        setRowsPerPage(nextRowsPerPage);
        updatePagination(0, nextRowsPerPage);
        writeCustomerCategoryTableSelection({
          customerId: selectedCustomer?.id ?? null,
          rowsPerPage: nextRowsPerPage,
        });
      }}
      rowsPerPageOptions={isMobile ? [] : [25, 50, 100, 250]}
      labelRowsPerPage={localizedTablePagination.labelRowsPerPage}
      labelDisplayedRows={localizedTablePagination.labelDisplayedRows}
      getItemAriaLabel={localizedTablePagination.getItemAriaLabel}
    />
  );

  const content = (
    <>
      <AdminFilterSummary
        filter={
          <MuiAutocompleteMultiSelect
            label={t("management.category.craveCategories.fields.selectCustomer")}
            options={customerOptions}
            value={selectedCustomer ? [selectedCustomer] : []}
            onChange={(next) => handleCustomerChange(next[0] ?? null)}
            getOptionLabel={(option) => option.label}
            getOptionKey={(option) => option.id}
            loading={isLoadingCustomers}
            disabled={isLoadingCustomers}
          />
        }
        count={categories.length}
        countLabel={t("management.category.craveCategories.text.totalCategories")}
        icon={<CategoryIcon color="primary" sx={{ fontSize: 40 }} />}
      />

      <AdminTablePanel
        header={
          <Box>
            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
              <TextField
                fullWidth
                label={t("management.category.craveCategories.search.label")}
                placeholder={t("management.category.craveCategories.search.placeholder")}
                value={searchQuery}
                onChange={(event) => {
                  setSearchQuery(event.target.value);
                  updatePagination(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label={t("common.clear")}
                        onClick={() => {
                          setSearchQuery("");
                          updatePagination(0);
                        }}
                        edge="end"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                }}
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setIsCreateDialogOpen(true)}
                disabled={!selectedCustomer}
                sx={{
                  alignSelf: { xs: "stretch", sm: "center" },
                  minHeight: 56,
                  px: 3,
                  whiteSpace: "nowrap",
                }}
              >
                {t("management.category.craveCategories.actions.add")}
              </Button>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", lg: "row" },
                alignItems: { xs: "flex-start", lg: "center" },
                gap: 1,
                mt: 1,
              }}
            >
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                <Autocomplete
                  disableClearable
                  getOptionKey={(option) => String(option.id)}
                  getOptionLabel={getStoreLinkOptionLabel}
                  loading={isStoresLoading}
                  onChange={(_event, value) => setDefaultStoreId(value.id)}
                  options={storeLinkOptions}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("management.category.craveCategories.fields.defaultStore")}
                    />
                  )}
                  size="small"
                  slotProps={{
                    paper: {
                      sx: {
                        width: { xs: "calc(100vw - 32px)", sm: 540 },
                      },
                    },
                  }}
                  sx={{ order: 0, width: { xs: "100%", sm: 360 } }}
                  value={selectedDefaultStore}
                />
                <Divider
                  flexItem
                  orientation="vertical"
                  sx={{ display: { xs: "none", sm: "block" }, mx: 1.5, order: 1 }}
                />
                <FormControlLabel
                  sx={{ color: "text.primary", order: 2 }}
                  control={
                    <Switch
                      checked={matchWholeWords}
                      onChange={(event) => {
                        setMatchWholeWords(event.target.checked);
                        updatePagination(0);
                      }}
                    />
                  }
                  label={t("management.category.craveCategories.controls.matchWholeWords")}
                />
              </Box>
              <Box
                sx={{
                  alignSelf: { xs: "flex-end", lg: "auto" },
                  marginInlineStart: { lg: "auto" },
                }}
              >
                {renderPagination()}
              </Box>
            </Box>
          </Box>
        }
        footer={renderPagination()}
        tableSx={{ minWidth: 760 }}
      >
        <TableHead>
          <TableRow>
            {(["id", "name", "createdAt"] as SortField[]).map((column) => (
              <SortableTableCell
                key={column}
                activeColumn={sortColumn}
                column={column}
                direction={sortDirection}
                onSort={(nextColumn) => {
                  handleSort(nextColumn);
                  updatePagination(0);
                }}
              >
                {t(`management.category.craveCategories.columns.${column}`)}
              </SortableTableCell>
            ))}
            <TableCell sx={{ textAlign: "end" }}>
              {t("management.category.craveCategories.columns.actions")}
            </TableCell>
          </TableRow>
        </TableHead>
        <AdminTableBodyState
          items={paginatedItems}
          isLoading={isLoadingCategories}
          colSpan={4}
          loadingLabel={t("common.loading")}
          emptyState={
            categoriesError !== null ? (
              <ErrorState
                title={categoriesError || t("management.category.craveCategories.errors.load")}
                onRetry={() => selectedCustomer && void fetchCategories(selectedCustomer.id)}
              />
            ) : (
              <AdminEmptyState
                icon={<CategoryIcon sx={{ fontSize: 40 }} />}
                text={
                  selectedCustomer
                    ? t("management.category.craveCategories.empty.noCategories")
                    : t("management.category.craveCategories.empty.selectCustomer")
                }
              />
            )
          }
        >
          {(category) => (
            <TableRow
              key={category.id}
              hover
              role="link"
              tabIndex={0}
              onClick={() => handleCategoryNavigation(category.name)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleCategoryNavigation(category.name);
                }
              }}
              sx={{
                cursor: "pointer",
                "&:hover, &.MuiTableRow-hover:hover": {
                  bgcolor: "action.hover",
                  "& .MuiTableCell-root": { bgcolor: "action.hover" },
                },
              }}
            >
              <TableCell>{category.id}</TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight="medium">
                  {category.name}
                </Typography>
              </TableCell>
              <TableCell>{formatStoreDate(category.createdAt, browserTimezone, locale)}</TableCell>
              <TableCell sx={{ textAlign: "end" }}>
                <AdminTableActions
                  actions={[
                    {
                      icon: <DeleteIcon />,
                      hint: t("management.category.craveCategories.actions.delete"),
                      ariaLabel: t("management.category.craveCategories.actions.delete"),
                      color: "error",
                      action: () => {
                        setDeleteError(null);
                        setCategoryToDelete(category);
                      },
                    },
                  ]}
                />
              </TableCell>
            </TableRow>
          )}
        </AdminTableBodyState>
      </AdminTablePanel>

      <AdminDialog
        icon={<StoreIcon />}
        maxWidth="sm"
        onClose={() => setStorePickerCategoryName(null)}
        open={storePickerCategoryName !== null}
        title={t("management.category.craveCategories.dialogs.selectStore")}
      >
        <AdminTablePanel elevation={0} tableProps={{ size: "small" }}>
          <TableHead>
            <TableRow>
              <TableCell>{t("commandCenter.fields.storeId")}</TableCell>
              <TableCell>{t("management.stores.stores.fields.storeName")}</TableCell>
            </TableRow>
          </TableHead>
          <AdminTableBodyState
            colSpan={2}
            emptyState={
              <AdminEmptyState
                icon={<StoreIcon sx={{ fontSize: 40 }} />}
                text={t("management.stores.stores.empty.noStores")}
              />
            }
            isLoading={isStoresLoading}
            items={customerStores}
            loadingLabel={t("common.loading")}
          >
            {(store) => (
              <TableRow
                hover
                key={store.id}
                onClick={() => {
                  if (storePickerCategoryName) {
                    openCategoryInStore(store.id, storePickerCategoryName);
                  }
                  setStorePickerCategoryName(null);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    if (storePickerCategoryName) {
                      openCategoryInStore(store.id, storePickerCategoryName);
                    }
                    setStorePickerCategoryName(null);
                  }
                }}
                role="button"
                sx={{
                  cursor: "pointer",
                  "&:hover, &.MuiTableRow-hover:hover": {
                    bgcolor: "action.hover",
                    "& .MuiTableCell-root": { bgcolor: "action.hover" },
                  },
                }}
                tabIndex={0}
              >
                <TableCell>{formatInteger(store.id, locale)}</TableCell>
                <TableCell>{getStoreLinkOptionLabel(store)}</TableCell>
              </TableRow>
            )}
          </AdminTableBodyState>
        </AdminTablePanel>
      </AdminDialog>

      <AdminDialog
        open={isCreateDialogOpen}
        onClose={closeCreateDialog}
        maxWidth="sm"
        title={t("management.category.craveCategories.createDialog.title")}
        icon={<AddIcon />}
        error={createError}
        errorOperation="create"
        errorEntity={t("management.category.craveCategories.entity")}
        cancelAction={{
          label: t("common.cancel"),
          onClick: closeCreateDialog,
          disabled: isCreating,
        }}
        primaryAction={{
          label: t("management.category.craveCategories.actions.add"),
          onClick: () => void handleCreate(),
          loading: isCreating,
        }}
      >
        <TextField
          label={t("management.category.craveCategories.fields.name")}
          value={newCategoryName}
          onChange={(event) => {
            setNewCategoryName(event.target.value);
            if (event.target.value.trim()) setIsCategoryNameInvalid(false);
          }}
          error={isCategoryNameInvalid}
          helperText={
            isCategoryNameInvalid
              ? t("management.category.craveCategories.validation.nameRequired")
              : undefined
          }
          fullWidth
          required
        />
      </AdminDialog>

      <AdminDialog
        open={Boolean(categoryToDelete)}
        onClose={() => {
          setCategoryToDelete(null);
          setDeleteError(null);
        }}
        title={t("management.category.craveCategories.deleteDialog.title")}
        icon={<DeleteIcon />}
        error={deleteError}
        errorOperation="delete"
        errorEntity={t("management.category.craveCategories.entity")}
        cancelAction={{
          label: t("common.cancel"),
          onClick: () => setCategoryToDelete(null),
          disabled: isDeleting,
        }}
        primaryAction={{
          label: t("management.category.craveCategories.actions.delete"),
          color: "error",
          onClick: () => void handleDelete(),
          loading: isDeleting,
        }}
      >
        <Typography>
          {t("management.category.craveCategories.deleteDialog.description", {
            categoryName: categoryToDelete?.name ?? "",
          })}
        </Typography>
      </AdminDialog>
    </>
  );

  if (embedded) return content;

  return (
    <PageShell
      breadcrumbs="admin"
      title={t("management.category.craveCategories.title")}
      icon={<CategoryIcon />}
    >
      {content}
    </PageShell>
  );
};

export default CraveCategories;
